// src/api/editions.ts
//
// A report's editions (plan 2026-10-23-an-edition-is-an-argument-keyed-
// snapshot, C4/C5; ADR-0496 D6): build a preview, list them, read one. An
// edition is the report's numbers frozen for a window, read from its own
// rows, so reading one never runs a query.
//
// The build goes through the dashboard mutation wrapper (`dashboardMutations`):
// it captures a version, so the page's version history refreshes exactly as
// after any other write. The types are the generated ones.
import { apiJson } from "./dashboardMutations";
import { ApiError } from "./client";
import type { components } from "@verity/shared-types";
import type { ItemPeriod } from "./dashboards";

export type EditionSummary = components["schemas"]["EditionSummaryDTO"];
export type EditionList = components["schemas"]["EditionListDTO"];
export type EditionDocument = components["schemas"]["EditionDocumentDTO"];
export type EditionPage = components["schemas"]["EditionPageDTO"];
export type EditionBlock = components["schemas"]["EditionBlockDTO"];
export type EditionStarted = components["schemas"]["EditionStartedDTO"];
/** The latest edition's words this caller may read (D7/D8). */
export type EditionText = components["schemas"]["EditionTextDTO"];
export type EditionBlockWrite = components["schemas"]["EditionBlockWriteDTO"];
/** The editor's sample words per written block, by block key (D8). */
export type SampleText = components["schemas"]["SampleTextDTO"];
export type SampleBlock = components["schemas"]["SampleBlockDTO"];

/** The edition statuses (`db/models/editions.py::EDITION_STATUSES`). */
export type EditionStatus = "QUEUED" | "RUNNING" | "READY" | "PARTIAL" | "FAILED";

/** The window an edition was built over (`window_json`). */
export type EditionWindow = { start: string; end: string; label: string };

/** One page of the list. */
export const EDITION_PAGE_SIZE = 20;

function base(dashboardId: string): string {
  return `/dashboards/${encodeURIComponent(dashboardId)}/editions`;
}

export function isEditionInFlight(status: string): boolean {
  return status === "QUEUED" || status === "RUNNING";
}

export function editionWindow(edition: Pick<EditionSummary, "window">): EditionWindow | null {
  const w = edition.window as Partial<EditionWindow> | null | undefined;
  return w && typeof w.start === "string" && typeof w.end === "string"
    ? { start: w.start, end: w.end, label: typeof w.label === "string" ? w.label : "" }
    : null;
}

/** Newest first. A preview is listed only for a caller who may edit. */
export async function listEditions(
  dashboardId: string,
  params: { page?: number; limit?: number } = {}
): Promise<EditionList> {
  const query = new URLSearchParams();
  query.set("page", String(params.page ?? 1));
  query.set("limit", String(params.limit ?? EDITION_PAGE_SIZE));
  return apiJson<EditionList>(`${base(dashboardId)}?${query.toString()}`);
}

/** One edition with its pages, blocks and stored answers. */
export async function getEdition(dashboardId: string, editionPublicId: string): Promise<EditionDocument> {
  return apiJson<EditionDocument>(`${base(dashboardId)}/${encodeURIComponent(editionPublicId)}`);
}

/** The words of the latest edition with text this caller may read: an editor
 *  any edition, a viewer a non-preview one (plan 2026-10-24, decision 7). Read
 *  once per report page, never per tile. */
export async function getEditionText(dashboardId: string): Promise<EditionText> {
  return apiJson<EditionText>(`/dashboards/${encodeURIComponent(dashboardId)}/edition-text`);
}

/** Invented words for every written block, for the editor only (404 for who
 *  may not change the report). */
export async function getSampleText(dashboardId: string): Promise<SampleText> {
  return apiJson<SampleText>(`/dashboards/${encodeURIComponent(dashboardId)}/sample-text`);
}

/** Start a preview edition of the report being edited. `period` overrides the
 *  report's default window for this preview only. */
export async function buildEdition(dashboardId: string, period?: ItemPeriod): Promise<EditionStarted> {
  return apiJson<EditionStarted>(base(dashboardId), {
    method: "POST",
    body: JSON.stringify(period ? { period } : {}),
  });
}

/** Why the server refused a build, from the `detail.code` the route sends. */
export type EditionBuildRefusal =
  // `edition` is null when the build lost a race and the winner's id is unknown.
  | { code: "edition_in_progress"; edition: string | null }
  | { code: "edition_cap_reached"; cap: string; limit: number }
  | { code: "edition_too_large"; queries: number; limit: number }
  | { code: "edition_window_empty" }
  // The period asked for, or the report's stored default when none was asked
  // for, no longer validates (plan 2026-10-26, D3): failed by name, never
  // built over the platform default.
  | { code: "edition_period_invalid" }
  | { code: "edition_default_period_invalid" }
  | { code: "edition_enqueue_failed" };

export function editionBuildRefusal(err: unknown): EditionBuildRefusal | null {
  if (!(err instanceof ApiError)) return null;
  const detail = err.detail as { code?: unknown } | null | undefined;
  if (!detail || typeof detail !== "object") return null;
  switch (detail.code) {
    case "edition_in_progress":
    case "edition_cap_reached":
    case "edition_too_large":
    case "edition_window_empty":
    case "edition_period_invalid":
    case "edition_default_period_invalid":
    case "edition_enqueue_failed":
      return detail as EditionBuildRefusal;
    default:
      return null;
  }
}
